import React, { useCallback, useState, type ComponentType } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import fetch from '@atlassian/jira-portfolio-3-portfolio/src/common/fetch/index.tsx';
import { PACKAGE_NAME, SERVICES_ERROR_ID, ERROR_REPORTING_TEAM } from '../constant.tsx';
import type { StatusTransition } from '../common/types.tsx';
import messages from './messages.tsx';
import type { OwnProps, Props } from './types.tsx';
import {
	getFilteredOrderedTransitions,
	addOriginalTransitions,
	removeTransitionsToCurrentAndOriginalStatus,
} from './utils.tsx';

export const transitionsProvider = (ComposedComponent: ComponentType<Props>) =>
	function (props: OwnProps) {
		const { issueKey, issueStatuses, issue } = props;
		const { formatMessage } = useIntl();
		const [loading, setLoading] = useState(false);
		const [transitions, setTransitions] = useState<StatusTransition[] | null>(null);
		const fetchTransitions = useCallback(() => {
			if (loading) {
				return;
			}
			setLoading(true);
			setTransitions(null);

			fetch(`/rest/api/2/issue/${issueKey}/transitions?sortByOpsBarAndStatus=true`, {
				method: 'GET',
			})
				.then((response) => {
					setLoading(false);
					if (response.ok) {
						return response.json();
					}
					throw new Error(`Response not ok. Statuscode: ${response.status}`);
				}) // eslint-disable-next-line @typescript-eslint/no-explicit-any
				.then((result: any) => {
					const transitionsFromServer = getFilteredOrderedTransitions(
						result.transitions || [],
						issue.statusId,
					);
					setTransitions(
						addOriginalTransitions(
							issue,
							removeTransitionsToCurrentAndOriginalStatus(
								issue,
								transitionsFromServer,
								issueStatuses,
							),
							issueStatuses,
							formatMessage(messages.originalTransitionStatusName),
						),
					);
				})
				.catch((err) => {
					setLoading(false);
					fireErrorAnalytics({
						error: err,
						meta: {
							id: SERVICES_ERROR_ID,
							packageName: PACKAGE_NAME,
							teamName: ERROR_REPORTING_TEAM,
						},
						attributes: {
							serviceName: 'fetchTransitions',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
					setTransitions(null);
				});
		}, [issueKey, issue, issueStatuses, loading, formatMessage]);

		return (
			<ComposedComponent
				{...props}
				transitions={transitions}
				loading={loading}
				fetchTransitions={fetchTransitions}
			/>
		);
	};
